import { cn, formatMNT, Input } from "@artisan/ui";
import { Check, ImageOff, Plus, RotateCw, Search, StickyNote, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import { api, ApiError, describeError, unwrap } from "../lib/api";
import type { Project, RenderItemDraft } from "../project/model";
import { type Asset, bestOffers, MAX_ITEMS, type Offer } from "./scene";

const KINDS = [
  { key: "all", label: "Бүгд" },
  { key: "material", label: "Материал" },
  { key: "fixture", label: "Тоноглол" },
  { key: "furniture", label: "Тавилга" },
  { key: "own", label: "Өөрийн" },
] as const;
type Kind = (typeof KINDS)[number]["key"];

function query(kind: Kind, q: string) {
  const base = { q: q.trim(), limit: 40 };
  switch (kind) {
    case "material":
      return { ...base, mode: "surface" };
    case "fixture":
      return { ...base, render_class: ["fixture", "appliance", "hardware"] };
    case "furniture":
      return { ...base, render_class: ["furniture"] };
    case "own":
      return { ...base, scope: "own" };
    default:
      return base;
  }
}

/** Unavailable because the server is not set up yet (no file store, no AI model). */
export function isSetupError(e: unknown): boolean {
  return e instanceof ApiError && (e.code === "storage_unavailable" || e.code === "storage_not_configured" || e.code === "render_unavailable");
}

export function SetupNotice({ code }: { code: string }) {
  return (
    <div className="grid gap-2 rounded-xl border border-dashed bg-card/70 p-5 text-sm">
      <p className="font-semibold">AI render энэ сервер дээр хараахан асаагүй байна</p>
      <p className="text-muted-foreground">
        {code === "render_unavailable"
          ? "AI загварын (Vertex AI) түлхүүр серверт тохируулагдаагүй байна."
          : "Зураг хадгалах сан (S3) серверт тохируулагдаагүй байна."}{" "}
        Админ тохируулсны дараа энэ хэсэг шууд ажиллана. Хэмжилт, илгээлт үүнээс хамаарахгүй.
      </p>
    </div>
  );
}

/** Search render-ready catalog items, with the best nearby price, and add them to the photo. */
export function CatalogTab({
  project,
  disabled,
  onAdd,
  onSelectPlaced,
}: {
  project: Project;
  disabled: boolean;
  onAdd: (a: Asset, offer: Offer | undefined) => void;
  onSelectPlaced: (i: number) => void;
}) {
  const [q, setQ] = useState("");
  const [kind, setKind] = useState<Kind>("all");
  const [assets, setAssets] = useState<Asset[] | null>(null);
  const [offers, setOffers] = useState<Map<string, Offer>>(new Map());
  const [error, setError] = useState<unknown>(null);
  const { lat, lng } = project.site;

  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      unwrap(api().GET("/v1/render/assets", { params: { query: query(kind, q) } }))
        .then(async (page) => {
          const items = page.items;
          if (!live) return;
          setAssets(items);
          setError(null);
          const best = await bestOffers(
            items.map((a) => a.product_id),
            { lat, lng },
          ).catch(() => new Map<string, Offer>());
          if (live) setOffers(best);
        })
        .catch((e) => {
          if (!live) return;
          setAssets(null);
          setError(e);
        });
    }, 250);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [q, kind, lat, lng]);

  const placed = new Map(project.render.items.map((it, i) => [it.asset_id, i]));
  const label = KINDS.find((k) => k.key === kind)?.label ?? "";

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="угаалтуур, фасад, тавцан…"
          aria-label="Каталогоос хайх"
          className="rounded-xl bg-card pl-10"
        />
      </div>
      <div className="flex gap-1 rounded-xl bg-muted p-1" role="group" aria-label="Ангилал">
        {KINDS.map((k) => (
          <button
            key={k.key}
            type="button"
            aria-pressed={kind === k.key}
            onClick={() => setKind(k.key)}
            className={cn(
              "h-8 flex-1 rounded-lg text-[13px] font-medium whitespace-nowrap",
              kind === k.key ? "bg-card text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {k.label}
          </button>
        ))}
      </div>
      {error ? (
        isSetupError(error) ? (
          <SetupNotice code={(error as ApiError).code} />
        ) : (
          <p className="text-sm text-destructive">{describeError(error)}</p>
        )
      ) : (
        <>
          <div className="flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
            <b className="text-sm font-semibold text-foreground">{q.trim() ? `«${q.trim()}»` : label}</b>
            <span>
              {assets ? `${assets.length} бүтээгдэхүүн` : "Хайж байна…"}
              {lat === undefined ? " · байршилгүй тул үнэгүй" : " · ойрын үнээр"}
            </span>
          </div>
          {assets && assets.length === 0 ? (
            <div className="grid gap-1.5 rounded-xl border border-dashed bg-card/70 p-5 text-sm">
              <p className="font-semibold">Render-т бэлэн бараа олдсонгүй</p>
              <p className="text-muted-foreground">
                Нийлүүлэгч бүтээгдэхүүнийхээ зургийг каталогт оруулахад зураг автоматаар render-т бэлтгэгдэж энд гарна.
              </p>
            </div>
          ) : null}
          <div className="-mr-2 grid min-h-0 flex-1 auto-rows-min grid-cols-2 gap-2.5 overflow-y-auto pr-2 pb-1">
            {(assets ?? []).map((a) => {
              const o = offers.get(a.product_id);
              const at = placed.get(a.id);
              const added = at !== undefined;
              return (
                <article key={a.id} className={cn("grid gap-0.5 rounded-[14px] border bg-card p-2 pb-2.5", added && "border-foreground ring-1 ring-foreground")}>
                  <div className="relative mb-1 grid h-[86px] place-items-center overflow-hidden rounded-[10px] bg-gradient-to-br from-[#f1f1f3] to-[#e4e4e8] dark:from-[#3a3a3f] dark:to-[#303035]">
                    {a.thumb_url ? (
                      <img src={a.thumb_url} alt="" className={cn("size-full", a.mode === "surface" ? "object-cover" : "object-contain p-1.5")} />
                    ) : (
                      <ImageOff className="size-6 text-muted-foreground" aria-hidden />
                    )}
                    {added ? (
                      <span className="absolute top-1.5 left-1.5 inline-flex h-5 items-center gap-1 rounded-full bg-foreground px-2 text-[10px] font-semibold text-background">
                        <Check className="size-3" aria-hidden />
                        {at + 1}-р байрлалд
                      </span>
                    ) : null}
                  </div>
                  <b className="truncate px-0.5 text-[13px] leading-[17px] font-semibold">{a.title_mn}</b>
                  <small className="truncate px-0.5 text-[11px] text-muted-foreground">
                    {o ? `${o.branch_name} · ${(o.distance_m / 1000).toFixed(1)} км` : a.org_id === project.org_id ? "Өөрийн бараа" : "Ойролцоо санал алга"}
                  </small>
                  <div className="mt-0.5 flex items-center justify-between pl-0.5">
                    <span className="text-sm font-semibold tabular">{o ? formatMNT(o.price_mnt) : "—"}</span>
                    <button
                      type="button"
                      disabled={!added && disabled}
                      onClick={() => (added ? onSelectPlaced(at) : onAdd(a, o))}
                      aria-label={added ? `${a.title_mn}: зураг дээр сонгох` : `${a.title_mn}: зураг дээр нэмэх`}
                      className={cn(
                        "grid size-9 place-items-center rounded-[9px] border disabled:opacity-40",
                        added ? "border-foreground bg-foreground text-background" : "bg-card hover:bg-accent",
                      )}
                    >
                      {added ? <Check className="size-4" aria-hidden /> : <Plus className="size-4" aria-hidden />}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/** The items on the photo, their prices and the total; the selected one can be adjusted. */
export function PlacedList({
  items,
  selected,
  onSelect,
  onChange,
  onRemove,
}: {
  items: RenderItemDraft[];
  selected: number | null;
  onSelect: (i: number | null) => void;
  onChange: (i: number, patch: Partial<RenderItemDraft>) => void;
  onRemove: (i: number) => void;
}) {
  const total = items.reduce((s, it) => s + (it.price_mnt ?? 0), 0);
  const chosen = selected === null ? undefined : items[selected];
  return (
    <section className="grid max-h-[46%] shrink-0 gap-1 overflow-y-auto border-t pt-2.5" aria-label="Зураг дээрх бараа">
      <div className="mb-0.5 flex items-baseline justify-between text-xs text-muted-foreground">
        <b className="text-sm font-semibold text-foreground">
          Зураг дээр · {items.length}/{MAX_ITEMS}
        </b>
        {total ? <span className="font-semibold text-foreground tabular">{formatMNT(total)}</span> : null}
      </div>
      {!items.length ? <p className="py-1 text-xs text-muted-foreground">Каталогоос «+» дарж барааг зураг дээр нэмнэ. Дараа нь чирж байрлуулна.</p> : null}
      {items.map((it, i) => (
        <button
          key={`${it.asset_id}-${i}`}
          type="button"
          onClick={() => onSelect(selected === i ? null : i)}
          aria-pressed={selected === i}
          className="grid grid-cols-[22px_minmax(0,1fr)_auto] items-center gap-2.5 rounded-md py-1 text-left text-xs"
        >
          <i className={cn("grid size-5 place-items-center rounded-full font-mono text-[10px] font-semibold not-italic", selected === i ? "bg-primary text-primary-foreground" : "bg-muted")}>
            {i + 1}
          </i>
          <span className="truncate font-semibold">
            {it.title}
            {it.supplier ? <span className="font-normal text-muted-foreground"> · {it.supplier}</span> : null}
          </span>
          <span className="font-semibold tabular">{it.price_mnt ? formatMNT(it.price_mnt) : "—"}</span>
        </button>
      ))}
      {chosen && selected !== null ? (
        <div className="mt-1 grid gap-2.5 rounded-xl border bg-card p-3">
          <label className="grid gap-1 text-xs font-medium text-muted-foreground">
            Хэмжээ · зургийн өргөний {Math.round(chosen.width * 100)}%
            <input
              type="range"
              min={4}
              max={100}
              value={Math.round(chosen.width * 100)}
              onChange={(e) => onChange(selected, { width: Number(e.target.value) / 100 })}
              className="accent-primary"
            />
          </label>
          <label className="grid gap-1 text-xs font-medium text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <RotateCw className="size-3" aria-hidden /> Эргэлт · {Math.round(chosen.rotation_deg)}°
            </span>
            <input
              type="range"
              min={-90}
              max={90}
              value={Math.round(chosen.rotation_deg)}
              onChange={(e) => onChange(selected, { rotation_deg: Number(e.target.value) })}
              className="accent-primary"
            />
          </label>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <StickyNote className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
              <Input
                value={chosen.note ?? ""}
                maxLength={500}
                onChange={(e) => onChange(selected, { note: e.target.value })}
                placeholder="AI-д тайлбар: өнгө, байрлал…"
                aria-label={`${selected + 1}-р барааны тайлбар`}
                className="pl-9"
              />
            </div>
            <button
              type="button"
              onClick={() => onRemove(selected)}
              aria-label="Зураг дээрээс хасах"
              className="grid size-11 place-items-center rounded-md border text-destructive hover:bg-destructive-soft"
            >
              <Trash2 className="size-4" aria-hidden />
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
